/**
 * useJourneyUnlockFeedback — Shows a toast when a journey step unlocks new features.
 *
 * Compares current completedSteps against localStorage to detect new completions.
 * Fires once per newly completed step, then persists so it won't repeat.
 *
 * Mounted in AppLayout so it runs globally for authenticated users.
 */

import { useEffect, useRef } from "react";
import { useUserProgression, type JourneyStep } from "@/hooks/useUserProgression";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const STORAGE_KEY = "journey-unlock-seen-step";

// Features unlocked at each step (only steps with journey-gated features)
const STEP_UNLOCKS: Partial<Record<JourneyStep, { title: string; features: string[] }>> = {
  3: {
    title: "Planning configuré !",
    features: ["Déplacer les séances entre les jours"],
  },
  4: {
    title: "Première séance terminée !",
    features: ["Création manuelle de programmes", "Analyse IA du programme"],
  },
  5: {
    title: "Mode standard activé !",
    features: ["Conseils IA de planning", "Détail de la fatigue"],
  },
};

function getSeenStep(userId: string): number {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}-${userId}`);
    return raw ? parseInt(raw, 10) : 0;
  } catch {
    return 0;
  }
}

function setSeenStep(userId: string, step: number) {
  try {
    localStorage.setItem(`${STORAGE_KEY}-${userId}`, String(step));
  } catch {
    // localStorage unavailable
  }
}

export function useJourneyUnlockFeedback() {
  const { user } = useAuth();
  const { completedSteps, loading } = useUserProgression();
  const shownRef = useRef(false);

  useEffect(() => {
    if (!user || loading || shownRef.current) return;

    const seen = getSeenStep(user.id);

    // Nothing new
    if (completedSteps <= seen) return;

    // Find steps that were just completed (between seen+1 and completedSteps)
    const newlyUnlocked: string[] = [];
    let latestTitle = "";

    for (let step = (seen + 1) as JourneyStep; step <= completedSteps; step++) {
      const unlock = STEP_UNLOCKS[step as JourneyStep];
      if (unlock) {
        latestTitle = unlock.title;
        newlyUnlocked.push(...unlock.features);
      }
    }

    if (newlyUnlocked.length > 0 && latestTitle) {
      shownRef.current = true;

      // Small delay so it doesn't compete with page transitions
      const timer = setTimeout(() => {
        toast.success(latestTitle, {
          description: newlyUnlocked.length === 1
            ? `Débloqué : ${newlyUnlocked[0]}`
            : `Débloqué : ${newlyUnlocked.join(", ")}`,
          duration: 5000,
        });
      }, 1200);

      setSeenStep(user.id, completedSteps);

      return () => clearTimeout(timer);
    } else {
      // No gated features at this step, just update seen
      setSeenStep(user.id, completedSteps);
    }
  }, [user, completedSteps, loading]);
}
